// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module Timeline
 * @package @geenius/analytics/react-css
 * @description Renders the React CSS analytics timeline for recent event
 * activity. The component maps shared timeline events into a compact log view
 * with event icons and localized timestamps.
 */

import { EVENT_ICONS, type TimelineEvent } from "@geenius/analytics";
import { Card, CardContent, EmptyState, Skeleton } from "@geenius/ui/react-css";
import type React from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the React CSS analytics timeline.
 *
 * @property events Timeline events to render in descending recency order.
 * @property maxVisible Maximum number of timeline rows to display.
 * @property ariaLabel Accessible label for the timeline log.
 * @property isLoading Whether the timeline should render placeholder rows.
 * @property className Optional class name merged into the timeline wrapper.
 */
export interface TimelineProps {
  events: TimelineEvent[];
  maxVisible?: number;
  ariaLabel?: string;
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  className?: string;
}

/**
 * Renders a React CSS timeline for recent analytics events.
 *
 * @param props Timeline events and the optional visible-row limit.
 * @returns A timeline log element for analytics activity.
 */
export function Timeline(props: TimelineProps): React.ReactElement {
  const copy = useAnalyticsCopy();
  const ariaLabel =
    props.ariaLabel ?? copy.t("analytics.dashboard.recentEvents");
  const visibleEvents = props.events.slice(0, props.maxVisible ?? 20);

  if (props.error) {
    return (
      <Card className={props.className}>
        <CardContent>
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  if (props.isLoading) {
    return (
      <Card className={props.className}>
        <CardContent>
          <div
            className="gn-analytics-timeline gn-analytics-timeline--loading"
            data-analytics-timeline-loading
            aria-busy="true"
            aria-label={ariaLabel}
            role="status"
          >
            {Array.from(
              { length: Math.min(props.maxVisible ?? 3, 5) },
              (_, index) => `timeline-skeleton-${index}`,
            ).map((skeletonId) => (
              <div
                key={skeletonId}
                className="gn-analytics-timeline__event gn-analytics-timeline__event--loading"
              >
                <Skeleton height={20} />
                <Skeleton height={14} />
                <Skeleton height={14} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={props.className}>
      <CardContent>
        {visibleEvents.length === 0 ? (
          <EmptyState title={copy.t("analytics.events.empty")} />
        ) : (
          <div
            aria-label={ariaLabel}
            aria-live="polite"
            aria-relevant="additions text"
            className="gn-analytics-timeline"
            role="log"
          >
            {visibleEvents.map((event) => (
              <div key={event.id} className="gn-analytics-timeline__event">
                <span
                  className="gn-analytics-timeline__icon"
                  aria-hidden="true"
                >
                  {EVENT_ICONS[event.event] ?? "⚡"}
                </span>
                <span className="gn-analytics-timeline__name">
                  {event.event}
                </span>
                {event.userId && (
                  <span className="gn-analytics-timeline__user">
                    {event.userId}
                  </span>
                )}
                <span className="gn-analytics-timeline__time">
                  {new Date(event.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
